import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Plus, Minus } from "../../icons/svg/outline";
import { itemMatchesSearchQuery } from "../../../utils/common/searchFilter";

/**
 * SearchableDropdown Component
 * A dropdown component with search/filter functionality similar to Asana's assignee selector
 *
 * Workflow:
 * 1. User clicks the input area
 * 2. Input becomes active and dropdown opens below
 * 3. User can either:
 *    - Type to filter options (press Enter to select highlighted item)
 *    - Use arrow keys to navigate and Enter to select
 *    - Click any item in the dropdown to select it
 * 4. Selected item is displayed in the input
 *
 * Keyboard Controls:
 * - Arrow Up/Down: Navigate through options
 * - Enter: Select highlighted option
 * - Escape: Close dropdown
 *
 * @param {Array} options - Array of option objects with {id, label, tooltip} structure (tooltip is optional)
 * @param {string|number} value - Currently selected option ID
 * @param {Function} onChange - Callback function when selection changes (receives option ID)
 * @param {string} placeholder - Placeholder text when no option is selected (changes to "Search..." when focused)
 * @param {boolean} disabled - Whether the dropdown is disabled
 * @param {string} className - Additional CSS classes for the container
 * @param {number} dropdownMinWidth - Optional minimum width for the dropdown in pixels (independent of input width)
 */
const SearchableDropdown = ({
  options = [],
  multiSelect = false,
  value,
  onChange,
  placeholder = "Select option",
  baseColor = "gray",
  disabled = false,
  className = "",
  dropdownMinWidth,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [keyboardIndex, setKeyboardIndex] = useState(null); // Separate index for keyboard navigation
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [hoveredOption, setHoveredOption] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  // Start with given value selected, if any
  const [selectedOptions, setSelectedOptions] = useState(value ? [value] : []);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const optionRefs = useRef({});

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4, // Just add a small gap (4px) for the mt-1 effect
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  // Add the current value to selectedOptions on mount (for multiSelect)
  useEffect(() => {
    if (multiSelect && value) {
      setSelectedOptions([value]);
    }
  }, [multiSelect, value]);


  // Scroll keyboard-navigated item into view when keyboardIndex changes
  useEffect(() => {
    if (isOpen && listRef.current && keyboardIndex !== null) {
      const keyboardItem = listRef.current.children[keyboardIndex];
      if (keyboardItem) {
        keyboardItem.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }
  }, [keyboardIndex, isOpen]);

  // Update dropdown position when scrolling
  useEffect(() => {
    const handleScroll = (event) => {
      // Ignore scroll events from the dropdown itself
      if (listRef.current && listRef.current.contains(event.target)) {
        return;
      }

      if (isOpen && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
        });
      }

      // Update tooltip position if an option is hovered
      if (hoveredOption && optionRefs.current[hoveredOption.id]) {
        const element = optionRefs.current[hoveredOption.id];
        const rect = element.getBoundingClientRect();
        setTooltipPosition({
          top: rect.top,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    window.addEventListener("scroll", handleScroll, true); // Use capture to catch all scroll events
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen, hoveredOption]);

  // If an ID is sent in, it was a single click selection. If not, it was a multi-select
  const handleClose = useCallback(
    (id) => {
      if (id) {
        onChange?.(id);
      } else {
        onChange?.(selectedOptions);
      }
      setIsOpen(false);
      setSearchTerm("");
      setKeyboardIndex(null);
      setHoveredOption(null);
    },
    [onChange, selectedOptions]
  );

  // Close dropdown when clicking outside (custom implementation for portal)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        listRef.current &&
        !listRef.current.contains(event.target)
      ) {
        // Save multi-select changes first
        if (multiSelect) {
          onChange?.(selectedOptions);
        }
        // Close dropdown
        setIsOpen(false);
        setSearchTerm("");
        setKeyboardIndex(null);
        setHoveredOption(null);
        // Reset selectedOptions back to current value (or empty if no value)
        // This ensures when it opens again, only the single item is shown as selected
        setSelectedOptions(value ? [value] : []);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, value, multiSelect, selectedOptions, onChange]);

  // Handle option hover
  const handleOptionHover = (option, element) => {
    if (option.tooltip && element) {
      const rect = element.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
      });
      setHoveredOption(option);
    }
  };

  // Handle option mouse leave
  const handleOptionLeave = () => {
    setHoveredOption(null);
  };

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    itemMatchesSearchQuery(option, searchTerm, ["label"])
  );

  // Reset keyboard index to 0 when search term changes (user is typing)
  // Use a ref to track previous search term to avoid resetting on arrow key navigation
  const prevSearchTermRef = useRef(searchTerm);
  useEffect(() => {
    if (isOpen && prevSearchTermRef.current !== searchTerm) {
      setKeyboardIndex(0);
      prevSearchTermRef.current = searchTerm;
    }
  }, [searchTerm, isOpen]);

  // Initialize keyboard index when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setKeyboardIndex(0);
    } else {
      setKeyboardIndex(null);
    }
  }, [isOpen]);

  // Get the currently selected option
  const selectedOption = options.find((opt) => opt.id === value);

  // Handle opening the dropdown
  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(true);
      setHoveredOption(null);
      // Focus the input when opening
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  // Handle selecting multiple options
  const handleSelect = (optionId) => {
    setSelectedOptions((prevSelected) => {
      if (prevSelected.includes(optionId)) {
        // Deselect if already selected
        return prevSelected.filter((id) => id !== optionId);
      }
      return [...prevSelected, optionId];
    });
  };

  // Handle clearing the selection
  const handleClear = (e) => {
    e.stopPropagation(); // Prevent opening the dropdown
    onChange?.(null);
    setSearchTerm("");
    setIsOpen(false);
    setHoveredOption(null);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      // Open dropdown if closed and arrow key is pressed
      e.preventDefault();
      handleOpen();
      return;
    }

    if (!isOpen) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setKeyboardIndex((prev) => {
        const currentIndex = prev === null ? -1 : prev;
        return currentIndex < filteredOptions.length - 1 ? currentIndex + 1 : currentIndex;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setKeyboardIndex((prev) => {
        const currentIndex = prev === null ? filteredOptions.length : prev;
        return currentIndex > 0 ? currentIndex - 1 : 0;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        // Use keyboardIndex if set, otherwise default to first option (index 0)
        const indexToSelect = keyboardIndex !== null ? keyboardIndex : 0;
        const optionToSelect = filteredOptions[indexToSelect];
        // Always select the highlighted item and close (single selection behavior)
        handleClose(optionToSelect.id);
        inputRef.current?.blur();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      setSearchTerm("");
      setKeyboardIndex(null);
      setHoveredOption(null);
      if (!multiSelect) {
        setSelectedOptions([]);
      }
      inputRef.current?.blur(); // Clear focus when closing
    }
  };

  // Get background class for option based on state
  const getOptionBGClass = (index, keyboardIndex, selectedOptions, optionId) => {
    // Keyboard navigation highlight
    if (index === keyboardIndex) {
      return `bg-gray-400 hover:bg-gray-500`;
    }
    // Multi-select highlight (blue)
    if (selectedOptions.includes(optionId)) {
      return `bg-blue-100 hover:bg-blue-200`;
    }
    // Default
    return `bg-gray-100 hover:bg-gray-200`;
  };

  const getIsSelected = (optionId) => {
    return multiSelect
      ? selectedOptions.includes(optionId)
      : value === optionId;
  };

  // Display text in the input
  const displayText = isOpen ? searchTerm : selectedOption?.label || "";

  // Dynamic placeholder based on dropdown state
  const dynamicPlaceholder = isOpen ? "Search..." : placeholder;

  return (
    <div className={`relative text-xs ${className}`} ref={containerRef}>
      {/* Input Field with Clear Button */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={displayText}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={handleOpen}
          onClick={handleOpen}
          onKeyDown={handleKeyDown}
          placeholder={dynamicPlaceholder}
          disabled={disabled}
          className={`w-full focus:caret-black h-[24px] px-[4%] pl-2 m-0 py-0 bg-${baseColor === "gray" ? "white" : baseColor}-100 focus:bg-white border rounded-md focus:outline-none focus:ring-2 focus:ring-${baseColor}-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer hover:bg-${baseColor}-200 transition-colors`}
        />
        {/* Clear Button - Only show when there's a selected value and not disabled */}
        {value && !disabled && !isOpen && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Dropdown List - Rendered via Portal */}
      {isOpen &&
        createPortal(
          <div
            ref={listRef}
            className="fixed z-[9999] bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              minWidth: dropdownMinWidth ? `${dropdownMinWidth}px` : undefined,
            }}
          >
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 font-inter text-xs">
                No options found
              </div>
            ) : (
              <ul>
                {filteredOptions.map((option, index) => (
                  <li key={option.id}>
                    <div className="flex items-center justify-between gap-0.5">
                      <span
                        onClick={() => {
                          handleClose(option.id);
                        }}
                        onMouseEnter={(e) =>
                          handleOptionHover(option, e.currentTarget)
                        }
                        onMouseLeave={handleOptionLeave}
                        className={`px-3 font-inter py-[2px] mt-1 w-full rounded-md cursor-pointer text-xs ${getOptionBGClass(
                          index,
                          keyboardIndex,
                          selectedOptions,
                          option.id
                        )}`}
                        ref={(el) => {
                          // Store ref for tooltip positioning
                          if (el) {
                            optionRefs.current[option.id] = el;
                          }
                        }}
                      >
                        {option.label}
                      </span>
                      {multiSelect && (
                        <button
                          onClick={() => handleSelect(option.id)}
                          className="flex-shrink-0 bg-blue-100 hover:bg-blue-200 mt-1 w-5 h-5 flex items-center justify-center rounded-md transition-colors font-bold"
                        >
                          {getIsSelected(option.id) ? (
                            <Minus className="w-3 h-3" />
                          ) : (
                            <Plus className="w-3 h-3" />
                          )}
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>,
          document.body
        )}

      {/* Tooltip - Rendered via Portal */}
      {hoveredOption?.tooltip &&
        createPortal(
          <div
            className="fixed font-inter z-[10000] bg-gray-100 shadow-lg shadow-gray-700 text-base px-2 py-1 rounded text-xs whitespace-normal max-w-xs pointer-events-none"
            style={{
              top: `${tooltipPosition.top - 8}px`, // Position above the option with some spacing
              left: `${tooltipPosition.left + tooltipPosition.width / 2}px`,
              transform: "translate(-50%, -100%)",
            }}
          >
            {hoveredOption.tooltip}
            {/* Arrow pointing down to the option */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-100"></div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default SearchableDropdown;
